/**
 * Prime the amenity icon library from the SFMC folder.
 *
 * Layne, 2026-08-21: "all the options are in this folder: Content Builder >
 * Test - Jack Blocks > Amenity Icons." The worker reads that folder
 * (/amenity-icons) and this pulls it once per mount into the synchronous store
 * `echAmen` keeps, so `echAmenLib()` stays callable during render.
 *
 * WHY A HOOK AND NOT A MODULE-LOAD FETCH: the store has to be able to trigger a
 * re-render when it lands. The picker reads the library synchronously while
 * rendering, so without React state a first paint that happened before the fetch
 * resolved would keep showing the 4-icon fallback until something else re-rendered
 * the tree.
 *
 * FAILURE IS SILENT ON PURPOSE. `echAmenLib()` falls back to the blocks' declared
 * defaults and then to the hardcoded four, so an unreachable worker costs breadth,
 * not function — the picker must never render zero icons, because that is what
 * degrades it to raw photo cards. The counts are returned for whoever wants to
 * surface them; nothing here decides to.
 */
import { useEffect, useState } from "react";
import { getAmenityIcons } from "./api";
import { setAmenFolderLib, amenFolderLib } from "./echAmen";

export interface AmenityLibState {
  /** icons the picker can offer right now — folder if it landed, fallback if not */
  count: number;
  /** assets in the folder with no hosted URL, straight off the worker */
  missing: number;
  status: "loading" | "ready" | "error";
}

/** one in-flight promise per page, so two mounts do not both hit the worker */
let once: Promise<void> | null = null;
let lastMissing = 0;

export function useAmenityIcons(): AmenityLibState {
  const [state, setState] = useState<AmenityLibState>(() => ({
    count: amenFolderLib().length,
    missing: lastMissing,
    /* already primed by an earlier mount — do not report "loading" and make a
       caller think the library is still on its way */
    status: amenFolderLib().length ? "ready" : "loading",
  }));

  useEffect(() => {
    let dead = false;
    if (!once) {
      once = getAmenityIcons()
        .then((d) => {
          if (d && d.ok && Array.isArray(d.icons)) {
            setAmenFolderLib(d.icons, d.groupOrder);
            lastMissing = Number(d.missingPublishedUrl) || 0;
          }
        })
        .catch(() => { /* fallback covers it — see the header */ });
    }
    once
      .then(() => { if (!dead) setState({ count: amenFolderLib().length, missing: lastMissing, status: "ready" }); })
      .catch(() => { if (!dead) setState((s) => ({ ...s, status: "error" })); });
    return () => { dead = true; };
  }, []);

  return state;
}

/** test seam only: drop the shared in-flight promise */
export function __resetAmenityIcons(): void { once = null; lastMissing = 0; }
